import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { HitmasterLogo } from "@/components/HitmasterLogo";
import { BackToLobby } from "@/components/BackToLobby";
import { LOBBY_VIDEO, getTheme, getStoredStyle } from "@/lib/themes";
import { LobbyMusic } from "@/components/LobbyMusic";
import { playWhoosh } from "@/lib/sfx";
import { useLang } from "@/i18n";
import { toast } from "sonner";

export default function JoinRoom() {
  const nav = useNavigate();
  const { t } = useLang();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  // Match the host's chosen style: fetch the room's style once we have a code
  // (via QR link or a full 6-char entry) and use that theme's lobby clip.
  const [bgVideo, setBgVideo] = useState(LOBBY_VIDEO);
  const [styleId, setStyleId] = useState(getStoredStyle());
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const tm = setTimeout(() => setBooting(false), 1600);
    return () => clearTimeout(tm);
  }, []);

  useEffect(() => {
    if (!code || code.length !== 6) return;
    let cancelled = false;
    api.get(`/rooms/${code}`)
      .then(({ data }) => {
        if (cancelled) return;
        const theme = getTheme(data.style);
        setStyleId(theme.id);
        setBgVideo(theme.getReadyVideo || theme.lobbyVideo || LOBBY_VIDEO);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [code]);

  const join = async () => {
    if (!code || code.length < 4) return toast.error(t("join.errValidCode"));
    if (!name.trim()) return toast.error(t("join.errName"));
    playWhoosh();
    // Request fullscreen synchronously with the user gesture so the browser allows it.
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) req.call(el).catch(() => {});
    } catch {}
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${code.trim().toUpperCase()}/join`, { playerName: name.trim() });
      localStorage.setItem(`hm_player_${data.roomCode}`, JSON.stringify({ playerId: data.playerId, name: name.trim() }));
      nav(`/play/${data.roomCode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("join.errFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen px-6 pb-6 flex flex-col items-center relative overflow-hidden"
      style={{
        paddingTop: "10px",
        backgroundImage: "linear-gradient(160deg, #0e6b53 0%, #12867a 35%, #1a6ca6 75%, #274690 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      <LobbyMusic styleId={styleId} />

      {/* Splash — Hitmaster logo + loader; fades into the Join screen for an app-like feel */}
      <div
        data-testid="join-splash"
        className="fixed inset-0 z-50 flex flex-col items-center justify-center transition-opacity duration-500"
        style={{
          background: "linear-gradient(160deg, #0e6b53 0%, #12867a 35%, #1a6ca6 75%, #274690 100%)",
          opacity: booting ? 1 : 0,
          pointerEvents: booting ? "auto" : "none",
        }}
      >
        <HitmasterLogo className="h-40 sm:h-52 animate-float" />
        <div className="mt-8 flex items-center gap-2" aria-label="loading">
          <span className="w-3 h-3 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-3 h-3 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-3 h-3 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
        <div className="mt-4 font-bebas uppercase tracking-[0.3em] text-white/80 text-sm">{t("join.loading")}</div>
      </div>

      {/* Looping video background — themed join-room clip (matches host style) */}
      <video
        data-testid="join-bg-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden
        key={bgVideo}
        className="animate-bg-fade"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
          pointerEvents: "none",
          filter: "saturate(1.22) contrast(1.06) brightness(1.03)",
        }}
        src={bgVideo}
      />
      {/* Same readability overlay as the landing screen */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(14, 18, 40, 0.28)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <div className="w-full max-w-md flex flex-col items-center mb-6 gap-3 relative transition-opacity duration-700" style={{ zIndex: 1, opacity: booting ? 0 : 1 }}>
        <HitmasterLogo className="h-32 sm:h-44 animate-float" />
        <BackToLobby />
      </div>
      <div className="tactile-cream p-6 w-full max-w-md space-y-4 relative transition-opacity duration-700" style={{ zIndex: 1, opacity: booting ? 0 : 1 }}>
        <h1 className="font-bebas text-navy text-5xl tracking-wide text-center">{t("join.title")}</h1>
        <div>
          <div className="font-display font-black text-navy text-sm uppercase mb-1">{t("join.roomCode")}</div>
          <input
            data-testid="join-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="ABCDEF"
            className="w-full bg-white/70 backdrop-blur-sm border-2 border-navy rounded-xl px-4 py-3 font-display font-black text-navy text-3xl tracking-widest text-center uppercase focus:outline-none focus:ring-4 focus:ring-coral/40"
          />
        </div>
        <div>
          <div className="font-display font-black text-navy text-sm uppercase mb-1">{t("join.yourName")}</div>
          <input
            data-testid="join-name-input"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 20))}
            placeholder="DJ Party"
            className="w-full bg-white/70 backdrop-blur-sm border-2 border-navy rounded-xl px-4 py-3 font-display font-black text-navy text-xl focus:outline-none focus:ring-4 focus:ring-teal/40"
          />
        </div>
        <button
          data-testid="join-submit-btn"
          onClick={join}
          disabled={busy}
          className="w-full bg-coral text-white font-display font-black text-2xl uppercase tracking-widest py-4 rounded-2xl border-4 border-navy active:translate-y-[2px] transition-all disabled:opacity-50"
          style={{ boxShadow: "0 14px 28px rgba(0,0,0,0.45), 0 6px 12px rgba(0,0,0,0.28)" }}
        >
          {t("join.start")}
        </button>
      </div>
    </div>
  );
}
